'use client';

import { useMemo } from 'react';
import {
  Select,
  SelectItem,
  SelectSection,
  Avatar,
  Button,
} from '@heroui/react';
import { FaTimes } from 'react-icons/fa';
import { FaUserPlus } from 'react-icons/fa6';
import { useScheduler } from '../SchedulerContext';

type InspectorFilterProps = {
  selectedInspectorIds: string[];
  onSelectionChange: (ids: string[]) => void;
  availableInspectorIds?: Set<string>;
};

export default function InspectorFilter({
  selectedInspectorIds,
  onSelectionChange,
  availableInspectorIds,
}: InspectorFilterProps) {
  const { inspectors, companyStyles } = useScheduler();

  // Filter inspectors to only show those with available slots
  const filteredInspectors = useMemo(() => {
    if (!availableInspectorIds || availableInspectorIds.size === 0) {
      return inspectors;
    }
    return inspectors.filter((inspector) =>
      availableInspectorIds.has(inspector._id)
    );
  }, [inspectors, availableInspectorIds]);

  const handleChange = (keys: any) => {
    if (keys === 'all') {
      onSelectionChange([]);
    } else {
      const selectedIds = Array.from(keys as Set<string>);
      onSelectionChange(selectedIds);
    }
  };

  return (
    <Select
      label='Preferred Inspectors'
      placeholder='All inspectors'
      selectionMode='multiple'
      selectedKeys={new Set(selectedInspectorIds)}
      onSelectionChange={handleChange}
      variant='bordered'
      size='sm'
      classNames={{
        base: 'w-64',
        trigger: ['bg-white', 'border', 'shadow-none'],
        label: '!font-light uppercase text-xs',
        value: 'text-themeBlack text-xs',
      }}
      isVirtualized={false}
      startContent={
        <FaUserPlus
          size={16}
          style={{
            color: companyStyles.companyColor || undefined,
          }}
        />
      }
      endContent={
        selectedInspectorIds.length > 0 && (
          <Button
            isIconOnly
            size='sm'
            variant='light'
            color='danger'
            onPress={() => onSelectionChange([])}
            aria-label='Clear inspector filter'
            className='min-w-unit-6 h-6 w-6'
          >
            <FaTimes size={12} />
          </Button>
        )
      }
    >
      <SelectSection title='Inspectors'>
        {filteredInspectors.map((inspector) => (
          <SelectItem key={inspector._id} textValue={inspector.name}>
            <div className='flex items-center gap-2'>
              <Avatar
                alt={inspector.name}
                className='flex-shrink-0'
                size='sm'
                src={inspector.thumb}
              />
              <div className='flex flex-col'>
                <span className='text-small'>{inspector.name}</span>
                <span className='text-tiny text-default-400'>
                  {inspector.title}
                </span>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectSection>
    </Select>
  );
}
